/**
 * Динамические WB-колонки «по регионам»: `wb_region_<metric>:<region>`.
 *
 * Полный аналог озоновских `cluster_<metric>:<name>`, но «регион» — это
 * федеральный округ WB (у остатка = округ склада из offices.federalDistrict,
 * у заказов = округ доставки oblastOkrugName). Данные берутся из wbLinkedData
 * (WbLinkedStats.stockByRegion / ordersByRegion7d / ordersByRegion30d) —
 * плюмбинг тот же, что у скалярных WB-колонок, потому что ключ начинается с
 * `wb_` (needsWb/hasWbColumns уже подтягивают wbLinkedData и прокидывают
 * wbLinkedStats в каждую ячейку).
 *
 * ЕДИНЫЙ источник правды по метрикам — этот модуль (в отличие от кластеров,
 * где список продублирован в 3 файлах). Модуль чистый (без БД/fs), поэтому
 * импортируется и клиентом, и сервером.
 */

import { WB_CATALOG_REGIONS } from "./wb-region-names";

/** Stable standard catalog plus any existing nonstandard keys; never drop saved regions. */
export function wbCatalogRegions(available: readonly string[] = []): string[] {
  return [...new Set([...WB_CATALOG_REGIONS, ...available.map(region => region.trim()).filter(Boolean)])];
}

export type WbRegionMetric = { key: string; label: string; description: string };

export const WB_REGION_METRICS: WbRegionMetric[] = [
  { key: "wb_region_stock",      label: "Остаток",    description: "Wildberries: остаток связанного WB-размера в этом регионе (склады региона)." },
  { key: "wb_region_orders_7d",  label: "Заказы 7д",  description: "Wildberries: заказы связанного WB-размера с доставкой в этот регион за 7 дней." },
  { key: "wb_region_orders_30d", label: "Заказы 30д", description: "Wildberries: заказы связанного WB-размера с доставкой в этот регион за 30 дней." },
];

export const WB_REGION_METRIC_KEYS = new Set(WB_REGION_METRICS.map(m => m.key));
export const WB_REGION_METRIC_LABELS: Record<string, string> = Object.fromEntries(WB_REGION_METRICS.map(m => [m.key, m.label]));
export const WB_REGION_METRIC_DESCRIPTIONS: Record<string, string> = Object.fromEntries(WB_REGION_METRICS.map(m => [m.key, m.description]));

/** Разбирает `wb_region_<metric>:<region>` → {metric, region} или null. */
export function parseWbRegionKey(key: string): { metric: string; region: string } | null {
  const sep = key.indexOf(":");
  if (sep <= 0) return null;
  const metric = key.slice(0, sep);
  if (!WB_REGION_METRIC_KEYS.has(metric)) return null;
  return { metric, region: key.slice(sep + 1) };
}

export function isWbRegionColumnKey(key: string): boolean {
  return parseWbRegionKey(key) !== null;
}

/** Нужны ли виду WB-данные (wbLinkedData): любая wb_-колонка ИЛИ суммарные
 *  колонки Ozon+WB (total_stock), которым тоже нужен WB-остаток. */
export function viewNeedsWbData(columns: string[]): boolean {
  return columns.some(c => c.startsWith("wb_") || c === "total_stock");
}

/** Скалярная WB-колонка (wb_stock, wb_ordered_7d/30d, wb_in_transit_*), но НЕ
 *  региональная. Для них в шапке/редакторе показываем бейдж «WB» вместо слова
 *  «WB» в названии. */
export function isWbScalarColumnKey(key: string): boolean {
  return key.startsWith("wb_") && !isWbRegionColumnKey(key);
}

/** Убирает префикс «WB » из подписи и капитализирует первую букву — для показа
 *  рядом с бейджем «WB» («WB остаток» → «Остаток»). В реестре подпись остаётся с
 *  «WB» (для экспорта/поиска/тултипов). */
export function stripWbLabelPrefix(label: string): string {
  const s = label.replace(/^WB\s+/i, "").trim();
  return s ? s.charAt(0).toUpperCase() + s.slice(1) : label;
}
